import { useState, FC, useMemo, useCallback, ChangeEvent, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useReactiveVar } from '@apollo/client';
import { LinearProgress, Avatar, Dialog, Drawer, Grid, Input, TextField, Typography } from '@material-ui/core';
import AccountCircle from '@material-ui/icons/AccountCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import styled from '@emotion/styled';

import { currentUserVar } from '../../../../cache';
import globalState from '../../../../operations/commands';
import { COLOR_TEXT_MAIN } from '../../../../theme/colors';
import {
  ChangeInstantImageDocument,
  ChangeInstantImageMutation,
  ChangeInstantImageMutationVariables,
  DeleteVariationDocument,
  DeleteVariationMutation,
  DeleteVariationMutationVariables,
  DeleteEditRequestDocument,
  DeleteEditRequestMutation,
  DeleteEditRequestMutationVariables,
  PostCommentDocument,
  PostCommentMutation,
  PostCommentMutationVariables,
  PostAttachmentDocument,
  PostAttachmentMutation,
  PostAttachmentMutationVariables,
  PostEditRequestDocument,
  PostEditRequestMutation,
  PostEditRequestMutationVariables,
  PutVariationDocument,
  PutVariationMutation,
  PutVariationMutationVariables,
  SubmitVariationDocument,
  SubmitVariationMutation,
  SubmitVariationMutationVariables,
  PossibleProject,
  PossibleVariation,
} from '../../../../generated/graphql';
import useEditor from '../../../../hooks/useEditor';
import useFileUpload from '../../../../hooks/useFileUpload';
import { Container as DrawerContainer, Footer as DrawerFooter } from '../../compound/Drawer';
import { ModalContent, ModalFooter, ModalText, ModalTitle } from '../../primitive/Modal';
import { BodySmall, BodyBold, LabelExtraSmallStyle } from '../../primitive/TypographyUI';
import { SPACING_SMALL } from '../../../constant/spacing';
import mutationVariables from '../../../../utility/mutationVariables';
import { DrawerHeader, DrawerTitle, DrawerContent, SecondaryButton, PrimaryButton } from './common';

type Props = {
  project: PossibleProject;
  variation: PossibleVariation;
  refetch: () => void;
};

const VariationActions: FC<Props> = ({ project, variation, refetch }) => {
  const { permissions } = variation;
  const openEditor = useEditor({ variation, project });
  const [interactionStatus, setInteractionStatus] = useState({
    changeSplitUrl: false,
    changeInstantImage: false,
    cancelEditRequest: false,
    deleteVariation: false,
    directModification: false,
    editRequest: false,
  });
  const toggleChangeSplitUrl = () => {
    setInteractionStatus((prevState) => ({
      ...prevState,
      changeSplitUrl: !prevState.changeSplitUrl,
    }));
  };
  const toggleChangeInstantImage = () => {
    setInteractionStatus((prevState) => ({
      ...prevState,
      changeInstantImage: !prevState.changeInstantImage,
    }));
  };
  const toggleDirectModification = () =>
    setInteractionStatus((prevState) => ({
      ...prevState,
      directModification: !prevState.directModification,
    }));
  const toggleCancelEditRequest = () =>
    setInteractionStatus((prevState) => ({
      ...prevState,
      cancelEditRequest: !prevState.cancelEditRequest,
    }));
  const toggleEditRequest = () =>
    setInteractionStatus((prevState) => ({
      ...prevState,
      editRequest: !prevState.editRequest,
    }));
  const toggleDelete = () =>
    setInteractionStatus((prevState) => ({
      ...prevState,
      deleteVariation: !prevState.deleteVariation,
    }));

  const onClickModify = () => {
    if (variation?.permissions?.open_direct_edit_confirmation?.permit) {
      toggleDirectModification();
    } else {
      openEditor({ editable: true });
    }
  };

  const canChangeUrl = useMemo(() => {
    if (project.project_type !== 'redirect_test') return false;
    if (variation.plan_a === true) return false;
    return true;
  }, [project, variation]);

  const canChangeInstantImage = useMemo(() => {
    if (variation.plan_a) return false;
    if (!variation.banner_image_url) return false;
    if (!permissions.update?.permit) return false;
    if (variation.display_popup) return false;
    return true;
  }, [project, variation]);

  const canEditVariation = useMemo(() => {
    if (variation.plan_a) return false;
    if (variation.banner_image_url && !variation.display_popup) return false;
    if (!permissions.update?.permit) return false;
    if (project.project_type === 'redirect_test') return false;
    return true;
  }, [project, variation]);

  const canCancelEditRequest = useMemo(() => {
    if (project.state === 'archived') return false;
    if (variation.reserved_score_base) return false;
    if (!permissions.cancel_edit_request?.permit) return false;
    return true;
  }, [project, variation]);

  const canCheckNewEdit = useMemo(() => {
    if (!variation.applicable_version_id) return false;
    if (project.state === 'archived') return false;
    if (permissions.create_edit_request?.permit) return false;
    return true;
  }, [project, variation]);

  const canRequestEdit = useMemo(() => {
    if (project.state === 'archived') return false;
    if (variation.reserved_score_base) return false;
    if (!permissions.create_edit_request?.permit) return false;
    return true;
  }, [project, variation]);

  return (
    <PaperFooter>
      {/* Actionable Buttons */}
      {canChangeUrl && <SecondaryButton onClick={toggleChangeSplitUrl}>デザイン案URLを変更する</SecondaryButton>}
      {canChangeInstantImage && (
        <SecondaryButton onClick={toggleChangeInstantImage}>デザイン案（画像）を変更する</SecondaryButton>
      )}
      {canEditVariation && <SecondaryButton onClick={onClickModify}>デザインを編集する</SecondaryButton>}
      {canCancelEditRequest && <PrimaryButton onClick={toggleCancelEditRequest}>修正依頼を取り消す</PrimaryButton>}
      {canCheckNewEdit && <PrimaryButton>修正を確認する</PrimaryButton>}
      {canRequestEdit && <PrimaryButton onClick={toggleEditRequest}>修正依頼をする</PrimaryButton>}
      {variation.state === 'draft' && <PrimaryRedButton onClick={toggleDelete}>削除する</PrimaryRedButton>}

      {/* Modals & Drawers */}
      <SplitUrlChangeModal
        variation={variation}
        refetch={refetch}
        isOpen={interactionStatus.changeSplitUrl}
        onClose={toggleChangeSplitUrl}
      />
      <InstantImageChangeDrawer
        variation={variation}
        project={project}
        refetch={refetch}
        isOpen={interactionStatus.changeInstantImage}
        onClose={toggleChangeInstantImage}
      />
      <DirectModificationModal
        project={project}
        variation={variation}
        isOpen={interactionStatus.directModification}
        onClose={toggleDirectModification}
      />
      <EditRequestDrawer
        variation={variation}
        refetch={refetch}
        isOpen={interactionStatus.editRequest}
        onClose={toggleEditRequest}
      />
      <CancelEditRequestModal
        variation={variation}
        refetch={refetch}
        isOpen={interactionStatus.cancelEditRequest}
        onClose={toggleCancelEditRequest}
      />
      <DeleteVariationModal
        projectId={project.id}
        variationId={variation.id}
        isOpen={interactionStatus.deleteVariation}
        onClose={toggleDelete}
      />
    </PaperFooter>
  );
};

export const SplitUrlChangeModal: FC<{
  variation: PossibleVariation;
  refetch: () => void;
  isOpen: boolean;
  onClose: () => void;
}> = ({ variation, refetch, isOpen, onClose }) => {
  const [urlString, setUrlString] = useState('');
  const [changeSplitUrl] = useMutation<PutVariationMutation, PutVariationMutationVariables>(PutVariationDocument, {
    context: { clientName: 'planbcd' },
  });

  const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => setUrlString(e?.target?.value);
  const onClickSave = () => {
    changeSplitUrl(
      mutationVariables({
        id: variation.id,
        variation: {
          redirect_url: urlString,
        },
      }),
    ).then(() => {
      refetch();
      onClose();
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ModalContent>
            <ModalTitle>別デザイン案URL</ModalTitle>
          </ModalContent>
          <ModalContent>
            <ModalText>別デザイン案URLを入力してください</ModalText>
            <Input type="text" onChange={onChangeInput} value={urlString} />
          </ModalContent>
        </Grid>
      </Grid>
      <ModalFooter>
        <SecondaryButton onClick={onClose}>キャンセル</SecondaryButton>
        <PrimaryButton onClick={onClickSave}>保存する</PrimaryButton>
      </ModalFooter>
    </Dialog>
  );
};

export const InstantImageChangeDrawer: FC<{
  variation: PossibleVariation;
  project: PossibleProject;
  refetch: () => void;
  isOpen: boolean;
  onClose: () => void;
}> = ({ variation, project, refetch, isOpen, onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [submitComment, setSubmitComment] = useState(variation.submit_comment);
  const [wordCount, setWordCount] = useState(0);
  const _checkFileSize = useCallback(
    (files: File[]): Promise<Error | null> => {
      return new Promise((resolve) => {
        if (files.length > 1 || !files.length) return resolve(Error('複数ファイル選択されています'));
        const file = files[0];
        const image = new Image();
        image.src = URL.createObjectURL(file);
        image.onload = async () => {
          if (imageSize.width !== image.width || imageSize.height !== image.height) {
            URL.revokeObjectURL(image.src);
            return resolve(Error('ファイルの大きさが一致しません'));
          } else {
            URL.revokeObjectURL(image.src);
            return resolve(null);
          }
        };
      });
    },
    [imageSize],
  );

  const { files, onDrop, removeFile } = useFileUpload({ customValidation: _checkFileSize });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const str = e.target.value.substring(0, 4000);
    setSubmitComment(str);
    setWordCount(str.length);
  };

  const [changeInstantImage] = useMutation<ChangeInstantImageMutation, ChangeInstantImageMutationVariables>(
    ChangeInstantImageDocument,
    {
      context: { clientName: 'planbcd' },
    },
  );
  const [submitVariation] = useMutation<SubmitVariationMutation, SubmitVariationMutationVariables>(
    SubmitVariationDocument,
    {
      context: { clientName: 'planbcd' },
    },
  );

  const canSubmit = !!files.length && !!submitComment;
  const onClickSubmit = () => {
    setIsProcessing(true);
    changeInstantImage(
      mutationVariables({
        id: variation.id,
        submit_comment: submitComment,
        data: files[0],
      }),
    )
      .then(({ data }) => {
        const { changeInstantImage: updatedInstantVariation } = data as ChangeInstantImageMutation;
        submitVariation(
          mutationVariables({
            id: project.id,
            variation: updatedInstantVariation,
          }),
        ).finally(() => {
          refetch();
          setIsProcessing(false);
          onClose();
        });
      })
      .catch(() => {
        globalState.setErrors({
          message: '画像を変更できませんでした',
          name: 'NonFatalError',
          context: 'planbcd',
        });
        setIsProcessing(false);
        onClose();
      })
      .finally(() => {
        setSubmitComment(variation.submit_comment);
        files.map((_, i) => removeFile(i));
      });
  };

  const SelectedImages = useMemo(
    () =>
      files.map((file, i) => (
        <Grid key={`selecetd-image-${i}`} container alignItems="center">
          <CancelIcon style={{ color: '#BF4140', cursor: 'pointer' }} onClick={() => removeFile(i)} />
          {file.type.indexOf('image/') !== 0 ? (
            <InsertDriveFileOutlinedIcon fontSize="large" />
          ) : (
            <UploadedImage src={window.URL.createObjectURL(file)} />
          )}
          <span>{file.name}</span>
        </Grid>
      )),
    [files],
  );

  useEffect(() => {
    if (!variation?.banner_image_url) {
      return;
    }
    const image = new Image();
    image.src = variation.banner_image_url;
    image.onload = () => {
      setImageSize({
        width: image.width,
        height: image.height,
      });
    };
  }, [variation.banner_image_url]);

  return (
    <Drawer anchor="right" open={isOpen}>
      <DrawerContainer>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>デザイン案（画像）の投稿</DrawerTitle>
          </DrawerHeader>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <BodySmall>以下サイズ仕様の画像をアップロードしてください。</BodySmall>
              <BodyBold>{`${imageSize.width}×${imageSize.height}`}</BodyBold>
            </Grid>
            <Grid item xs={12}>
              {/* @ts-ignore: ts(2322) component prop does accept html tag names */}
              <SecondaryButton component="label">
                アップロードする素材・資料を選択する
                <input hidden id="attachment" name="attachment" type="file" accept="*/*" multiple onChange={onDrop} />
              </SecondaryButton>
              {SelectedImages}
            </Grid>
            <Grid container item alignItems="center">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {/* コメント入力エリア */}
                  <CommentLabel>制作意図</CommentLabel>
                  <CommentField variant={`outlined`} multiline minRows={5} value={submitComment} onChange={onChange} />
                  <WordCounter>{wordCount}/4000</WordCounter>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DrawerContent>
        <DrawerFooter>
          <BodySmall>
            デザイン案投稿後の修正は行えませんので、予めご了承ください。よろしければ、「デザイン案を投稿する」ボタンを押してください。
          </BodySmall>
          <SecondaryButton onClick={onClose}>キャンセル</SecondaryButton>
          <PrimaryButton disabled={!canSubmit} onClick={onClickSubmit}>
            デザイン案を投稿する
          </PrimaryButton>
        </DrawerFooter>
        {isProcessing && <LinearProgress />}
      </DrawerContainer>
    </Drawer>
  );
};

export const DirectModificationModal: FC<{
  variation: PossibleVariation;
  project: PossibleProject;
  isOpen: boolean;
  onClose: () => void;
}> = ({ variation, project, isOpen, onClose }) => {
  const openEditor = useEditor({ variation, project });

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalTitle>編集する上での注意</ModalTitle>
      </ModalContent>
      <ModalContent>
        <BodySmall>
          グロースハッカーが作成したデザインをご自身で編集される場合はデザイン審査は行われません。ご自身で十分にご確認のうえ、反映お願いします。
        </BodySmall>
      </ModalContent>
      <ModalContent>
        <BodySmall>参考</BodySmall>
        <BodySmall>
          <a
            href="https://support.kaizenplatform.net/hc/ja/articles/206220841"
            style={{ color: '#006BD6', textDecoration: 'none' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            デザインチェックシート
          </a>
        </BodySmall>
        <BodySmall>
          <a
            href="http://support.kaizenplatform.net/hc/ja/articles/206220901"
            style={{ color: '#006BD6', textDecoration: 'none' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            コードチェックシート
          </a>
        </BodySmall>
      </ModalContent>
      <ModalFooter>
        <SecondaryButton onClick={onClose}>キャンセル</SecondaryButton>
        <PrimaryButton onClick={() => openEditor({ editable: true })}>OK</PrimaryButton>
      </ModalFooter>
    </Dialog>
  );
};

export const EditRequestDrawer: FC<{
  variation: PossibleVariation;
  refetch: () => void;
  isOpen: boolean;
  onClose: () => void;
}> = ({ variation, refetch, isOpen, onClose }) => {
  const { files, onDrop, removeFile } = useFileUpload();
  const [isProcessing, setIsProcessing] = useState(false);
  const [postComment] = useMutation<PostCommentMutation, PostCommentMutationVariables>(PostCommentDocument, {
    context: { clientName: 'planbcd' },
  });

  const [postAttachements] = useMutation<PostAttachmentMutation, PostAttachmentMutationVariables>(
    PostAttachmentDocument,
    { context: { clientName: 'planbcd' } },
  );

  const [postEditRequest] = useMutation<PostEditRequestMutation, PostEditRequestMutationVariables>(
    PostEditRequestDocument,
    { context: { clientName: 'planbcd' } },
  );
  const [comment, setComment] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const str = e.target.value.substring(0, 4000);
    setComment(str);
    setWordCount(str.length);
  };

  const onClickRequest = () => {
    if (!comment) {
      return globalState.setErrors({
        message: 'コメントを入力してください',
        name: 'NonFatalError',
        context: 'planbcd',
      });
    }
    setIsProcessing(true);
    postComment(
      mutationVariables({
        id: variation.id,
        variation_comment: {
          with_action: 'create_edit_request',
          comment: comment,
        },
      }),
    )
      .then(({ data }) => {
        const { postComment: postCommentData } = data as PostCommentMutation;
        Promise.all(
          files.map((file) => {
            return postAttachements(
              mutationVariables({
                id: postCommentData.id,
                data: file,
              }),
            );
          }),
        ).then(() => {
          postEditRequest(
            mutationVariables({
              comment: postCommentData,
              id: variation.id,
            }),
          ).finally(() => {
            refetch();
            onClose();
            setIsProcessing(false);
          });
        });
      })
      .catch((error) => {
        globalState.setErrors({
          message: `修正依頼できませんでした: (${error.message})`,
          name: 'NonFatalError',
          context: 'planbcd',
        });
        onClose();
        setIsProcessing(false);
      });
  };

  const SelectedImages = useMemo(
    () =>
      files.map((file, i) => (
        <Grid key={`selecetd-image-${i}`} container alignItems="center">
          <CancelIcon style={{ color: '#BF4140', cursor: 'pointer' }} onClick={() => removeFile(i)} />
          {file.type.indexOf('image/') !== 0 ? (
            <InsertDriveFileOutlinedIcon fontSize="large" />
          ) : (
            <UploadedImage src={window.URL.createObjectURL(file)} />
          )}
          <span>{file.name}</span>
        </Grid>
      )),
    [files],
  );

  return (
    <Drawer anchor="right" open={isOpen}>
      <DrawerContainer>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>修正を依頼する</DrawerTitle>
          </DrawerHeader>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <BodySmall>
                グロースハッカーへ修正依頼をします。
                <br />
                グロースハッカーへ伝える修正依頼の内容や背景、目的を記載してください。
              </BodySmall>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <CommentLabel>
                    コメント<RedText>必須</RedText>
                  </CommentLabel>
                  <CommentField variant={`outlined`} multiline minRows={5} value={comment} onChange={onChange} />
                  <WordCounter>{wordCount}/4000</WordCounter>
                </Grid>
              </Grid>
            </Grid>
            <Grid container item alignItems="center">
              {/* @ts-ignore: ts(2322) component prop does accept html tag names */}
              <SecondaryButton component="label">
                アップロードする素材・資料を選択する
                <input hidden id="attachment" name="attachment" type="file" accept="*/*" multiple onChange={onDrop} />
              </SecondaryButton>
              <UploadLabel>最大50MB / 最大3ファイル</UploadLabel>
            </Grid>
            <Grid item xs={12}>
              <BodySmall>
                資料や制作素材などのデータを添付ファイルとして追加できます。
                <br />
                最大3ファイルまでアップロードいただけます
              </BodySmall>
            </Grid>
            {SelectedImages}
          </Grid>
        </DrawerContent>
        <DrawerFooter>
          <SecondaryButton onClick={onClose}>キャンセル</SecondaryButton>
          <PrimaryButton onClick={onClickRequest} disabled={isProcessing}>
            修正を依頼する
          </PrimaryButton>
        </DrawerFooter>
        {isProcessing && <LinearProgress />}
      </DrawerContainer>
    </Drawer>
  );
};

export const CancelEditRequestModal: FC<any> = ({ variation, refetch, isOpen, onClose }) => {
  const currentUser = useReactiveVar(currentUserVar)!;
  const [isProcessing, setIsProcessing] = useState(false);
  const [comment, setComment] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const str = e.target.value.substring(0, 4000);
    setComment(str);
    setWordCount(str.length);
  };

  const [postComment] = useMutation<PostCommentMutation, PostCommentMutationVariables>(PostCommentDocument, {
    context: { clientName: 'planbcd' },
  });

  const [cancelEditRequestMutation] = useMutation<DeleteEditRequestMutation, DeleteEditRequestMutationVariables>(
    DeleteEditRequestDocument,
    {
      context: { clientName: 'planbcd' },
    },
  );

  const cancelEditRequest = () => {
    setIsProcessing(true);
    Promise.all([
      postComment(
        mutationVariables({
          id: variation.id,
          variation_comment: {
            with_action: 'cancel_edit_request',
            comment: comment,
          },
        }),
      ),
    ])
      .then(() => {
        cancelEditRequestMutation(
          mutationVariables({
            id: variation.id,
          }),
        ).finally(() => {
          refetch();
          onClose();
          setIsProcessing(true);
        });
      })
      .catch((error) => {
        globalState.setErrors({
          message: `修正依頼キャンセルができませんでした: (${error.message})`,
          name: 'NonFatalError',
          context: 'planbcd',
        });
        setIsProcessing(true);
        onClose();
      });
  };

  return (
    <Dialog maxWidth="md" open={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalTitle>修正依頼を取り消す</ModalTitle>
        <AvatorFlex>
          {currentUser.profileImageUrl ? (
            <Avatar src={currentUser.profileImageUrl} />
          ) : (
            <Avatar>
              <AccountCircle />
            </Avatar>
          )}
          <BodyBold>{currentUser.name}</BodyBold>
        </AvatorFlex>
        <BodySmall>
          グロースハッカーへ依頼した修正依頼を取り消します。修正依頼を取り消すと最後に反映されたデザイン案がこのまま配信されます。
        </BodySmall>
        <Grid item xs={12}>
          <CommentLabel>
            コメント<RedText>必須</RedText>
          </CommentLabel>
          <CommentField variant={`outlined`} multiline minRows={5} value={comment} onChange={onChange} />
          <WordCounter>{wordCount}/4000</WordCounter>
        </Grid>
      </ModalContent>
      {isProcessing && (
        <Grid item>
          <LinearProgress />
        </Grid>
      )}
      <ModalFooter>
        <SecondaryButton onClick={onClose}>キャンセル</SecondaryButton>
        <PrimaryButton onClick={cancelEditRequest} disabled={isProcessing}>
          修正依頼を取り消す
        </PrimaryButton>
      </ModalFooter>
    </Dialog>
  );
};

export const DeleteVariationModal: FC<any> = ({ projectId, variationId, isOpen, onClose }) => {
  const navigate = useNavigate();

  const [deleteVariations] = useMutation<DeleteVariationMutation, DeleteVariationMutationVariables>(
    DeleteVariationDocument,
    {
      context: { clientName: 'planbcd' },
      ...mutationVariables({
        id: variationId,
      }),
    },
  );
  const onDelete = () => {
    deleteVariations();
    onClose();
    setTimeout(() => {
      navigate(`/engine/experiments/${projectId}?tab=variations`);
    }, 3000);
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ModalContent>
            <ModalTitle>投稿の削除</ModalTitle>
          </ModalContent>

          <ModalContent>
            <ModalText>
              投稿を削除します。投稿を削除すると、元に戻すことができません。
              <br />
              よろしければ、削除するボタンをクリックしてください。
            </ModalText>
          </ModalContent>
        </Grid>
      </Grid>
      <ModalFooter>
        <SecondaryButton onClick={onClose}>キャンセル</SecondaryButton>
        <PrimaryButton onClick={onDelete}>OK</PrimaryButton>
      </ModalFooter>
    </Dialog>
  );
};

const PaperFooter = styled('div')(`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  gap: 1rem;
`);

const PrimaryRedButton = styled(PrimaryButton)(`
  background: #BF4140;
  :hover {
    background: #EB5757;
    border-color: #EB5757;
  }
`);

const UploadLabel = styled('p')({
  ...LabelExtraSmallStyle,
  marginLeft: `${SPACING_SMALL}px`,
});

const UploadedImage = styled('img')({
  maxHeight: '120px',
  margin: '1rem',
  display: 'block',
});

const AvatorFlex = styled('div')(`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.8rem;
  margin: 1.6rem 0;
`);

const CommentLabel = styled(Typography)(`
  font-size: 12px;
  color: ${COLOR_TEXT_MAIN};
  margin-bottom: 9px;
`);

const RedText = styled('span')(`
  color: #BF4140;
  padding-left: 4px;
`);

const CommentField = styled(TextField)(`
  width: 100%;
  border-radius: 8px;
  '& .MuiOutlinedInput-root': {
    color: ${COLOR_TEXT_MAIN};
    border-radius: 8px;
    font-size: 14px;
    height: 118px;
    '& fieldset': {
      outline: none;
      border: 1px solid #E6E7E8;
    };
    '&:hover fieldset': {
      outline: none;
      border: 1.5px solid #E6E7E8;
    };
    '&.Mui-focused fieldset': {
      outline: none;
      border: 1.5px solid #707889;
    };
    '&.Mui-error fieldset': {
      border: 1.5px solid #FE7877;
    };
    '&.Mui-error input': {
      color: #FE7877;
    };
  };
  '&.MuiOutlinedInput-notchedOutline': {
    fieldset: {
      outline: none;
      border: 1.5px solid #707889;
    };
  };
`);

const WordCounter = styled('span')(`
  font-size: 10px;
  margin-top: 6px;
  float: right;
`);

export default VariationActions;
